import type { IContactPerson } from '@condo/domain';

/** Minimal PUT /contacts/:id body for clearing avatar (basecamp requires contactType + name fields). */
export function toContactAvatarRemovalPayload(
    contact: Pick<IContactPerson, 'contactId' | 'contactType' | 'firstname' | 'lastname'>,
): Pick<IContactPerson, 'contactId' | 'contactType' | 'firstname' | 'lastname' | 'avatarMediaFileId'> {
    return {
        contactId: contact.contactId,
        contactType: contact.contactType,
        firstname: contact.firstname,
        lastname: contact.lastname,
        avatarMediaFileId: null,
    };
}

/** Minimal PUT /contacts/:id body for assigning avatar after media link (same Joi fields as removal). */
export function toContactAvatarAssignmentPayload(
    contact: Pick<IContactPerson, 'contactId' | 'contactType' | 'firstname' | 'lastname'>,
    avatarMediaFileId: number,
): Pick<IContactPerson, 'contactId' | 'contactType' | 'firstname' | 'lastname' | 'avatarMediaFileId'> {
    return {
        contactId: contact.contactId,
        contactType: contact.contactType,
        firstname: contact.firstname,
        lastname: contact.lastname,
        avatarMediaFileId,
    };
}

export interface ApproverItem {
    contactId: number;
    fullname?: string;
    email?: string;
}

/**
 * Format a contact's name for display
 * @param contact - Contact object
 * @returns Formatted name (fullname or email or 'Unknown')
 */
export function formatContactName(contact: IContactPerson): string {
    const fullName = contact.fullname ?? `${contact.firstname ?? ''} ${contact.lastname ?? ''}`.trim();
    const parts = fullName.trim().split(' ');
    const firstName = parts[0] || '';
    const lastName = parts[parts.length - 1] || '';
    return `${firstName} ${lastName}`;
}

/** Display name for timelines and labels: full name, then parts, then email. */
export function formatContactPersonLabel(contact: Pick<IContactPerson, 'fullname' | 'firstname' | 'lastname' | 'email'>): string {
    const fromFull = contact.fullname?.trim();
    if (fromFull) {
        return fromFull;
    }
    const fromParts = `${contact.firstname ?? ''} ${contact.lastname ?? ''}`.trim();
    if (fromParts) {
        return fromParts;
    }
    const fromEmail = contact.email?.trim();
    if (fromEmail) {
        return fromEmail;
    }
    return '';
}

/**
 * Convert an approver item to a Contact object
 */
export function approverToContact(approver: ApproverItem): IContactPerson {
    return {
        contactId: approver.contactId,
        fullname: approver.fullname,
        email: approver.email,
    } as IContactPerson;
}

/**
 * Convert a Contact object to an approver item
 */
export function contactToApprover(contact: IContactPerson): ApproverItem {
    return {
        contactId: contact.contactId!,
        fullname: contact.fullname,
        email: contact.email,
    };
}

/**
 * Convert a signature object to a Contact object
 */
export function signatureToContact(sig: { contactId: number; fullname?: string; email?: string }): IContactPerson {
    return {
        contactId: sig.contactId,
        fullname: sig.fullname,
        email: sig.email,
    } as IContactPerson;
}
